import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './AdminLayout.css';

const AdminLayout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Cerrar dropdown al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Cerrar sidebar en móvil al cambiar de ruta
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const menuItems = [
        {
            path: '/admin/usuarios',
            icon: '👥',
            label: 'Pacientes',
            description: 'Gestionar pacientes'
        },
        {
            path: '/admin/servicios',
            icon: '💆',
            label: 'Servicios',
            description: 'Tipos de masaje y precios'
        },
        {
            path: '/admin/turnos',
            icon: '📅',
            label: 'Turnos',
            description: 'Ver todos los turnos'
        },
        {
            path: '/admin/masajistas',
            icon: '🧘',
            label: 'Masajistas',
            description: 'Gestionar profesionales'
        },
        {
            path: '/admin/pagos',
            icon: '💰',
            label: 'Pagos',
            description: 'Historial de pagos y señas'
        },
        {
            path: '/admin/reportes',
            icon: '📊',
            label: 'Reportes',
            description: 'Estadísticas y reportes'
        },
        { 
    path: '/admin/agenda', 
    icon: '🗓️', 
    label: 'Agenda / Ausencias', 
    description: 'Ver disponibilidad de masajistas' 
}
    ];

    // Iniciales del usuario
    const getInitials = () => {
        if (!user) return '?';
        const n = user.nombre?.trim();
        const a = user.apellido?.trim();
        if (n && a) return `${n.charAt(0)}${a.charAt(0)}`.toUpperCase();
        if (n) return n.charAt(0).toUpperCase();
        if (user.email) return user.email.charAt(0).toUpperCase();
        return '?';
    };

    const getNombreCompleto = () => {
        if (!user) return '';
        const nombre = [user.nombre, user.apellido].filter(Boolean).join(' ').trim();
        return nombre || user.email;
    };

    const getRolLabel = () => {
        if (!user) return '';
        const roles = { admin: 'Administrador', masajista: 'Masajista', paciente: 'Paciente' };
        return roles[user.role] || user.role;
    };

    const getPageTitle = () => {
        return menuItems.find(item => location.pathname.startsWith(item.path))?.label || 'Panel';
    };

    return (
        <div className="admin-layout">
            {/* Botón hamburguesa para móvil */}
            <button
                className="admin-mobile-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label="Abrir menú"
            >
                <span className={`admin-toggle-icon ${sidebarOpen ? 'open' : ''}`}>
                    <span></span>
                    <span></span>
                    <span></span>
                </span>
            </button>

            {/* Overlay para móvil */}
            {sidebarOpen && (
                <div className="admin-overlay" onClick={() => setSidebarOpen(false)}></div>
            )}

            {/* Sidebar vertical */}
            <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
                {/* Logo */}
                <div className="sidebar-header">
                    <Link to="/" className="sidebar-logo">
                        <span className="sidebar-logo-icon">🧘</span>
                        <span className="sidebar-logo-text">Tu Masaje</span>
                    </Link>
                    <div className="sidebar-subtitle">Panel de Administración</div>
                </div>

                {/* Menú */}
                <nav className="sidebar-nav">
                    <ul className="sidebar-menu">
                        {menuItems.map((item) => {
                            const isActive = location.pathname.startsWith(item.path);
                            return (
                                <li key={item.path} className="sidebar-menu-item">
                                    <Link
                                        to={item.path}
                                        className={`sidebar-menu-link ${isActive ? 'active' : ''}`}
                                    >
                                        <span className="menu-icon">{item.icon}</span>
                                        <div className="menu-content">
                                            <span className="menu-label">{item.label}</span>
                                            <span className="menu-description">{item.description}</span>
                                        </div>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                {/* Datos del usuario (pie de la barra lateral) */}
                <div className="sidebar-footer" ref={dropdownRef}>
                    {menuOpen && (
                        <div className="sidebar-user-dropdown">
                            <Link
                                to="/perfil"
                                className="sidebar-dropdown-item"
                                onClick={() => setMenuOpen(false)}
                            >
                                <span className="dropdown-item-icon">👤</span>
                                Mi Perfil
                            </Link>
                            <Link
                                to="/perfil?tab=password"
                                className="sidebar-dropdown-item"
                                onClick={() => setMenuOpen(false)}
                            >
                                <span className="dropdown-item-icon">🔑</span>
                                Cambiar Contraseña
                            </Link>
                            <div className="sidebar-dropdown-divider"></div>
                            <button
                                className="sidebar-dropdown-item sidebar-dropdown-logout"
                                onClick={handleLogout}
                            >
                                <span className="dropdown-item-icon">🚪</span>
                                Cerrar Sesión
                            </button>
                        </div>
                    )}

                    <button
                        className="sidebar-user-btn"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-expanded={menuOpen}
                    >
                        <div className="sidebar-user-avatar">
                            {getInitials()}
                        </div>
                        <div className="sidebar-user-info">
                            <div className="sidebar-user-name">{getNombreCompleto()}</div>
                            <div className="sidebar-user-role">{getRolLabel()}</div>
                        </div>
                        <span className={`sidebar-user-caret ${menuOpen ? 'open' : ''}`}>▴</span>
                    </button>
                </div>
            </aside>

            {/* Contenido principal */}
            <main className="admin-main-content">
                <div className="admin-content-header">
                    <div className="admin-breadcrumb">
                        <span className="breadcrumb-item">Admin</span>
                        <span className="breadcrumb-separator">/</span>
                        <span className="breadcrumb-item active">{getPageTitle()}</span>
                    </div>
                </div>
                <div className="admin-content-body">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;